import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import {
    collection, doc, getDoc, setDoc
} from 'firebase/firestore';
import Header from "../components/header";
import Footer from "../components/Footer";
import { motion, AnimatePresence } from 'framer-motion';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import styled from 'styled-components';
import { DragDropContext } from 'react-beautiful-dnd';
import { FaTachometerAlt, FaBuilding, FaCog, FaInbox, FaChevronRight, FaSignOutAlt } from 'react-icons/fa';
// Import dark mode hook
import { useDarkMode } from '../Functions/DarkMode';
import { getSiteStatistics } from '../Functions/TrackingFunctions';
import { getAllBookingEnquiries } from '../Functions/FetchEnquiries';
import { useNavigate } from 'react-router-dom';
import { checkAuthState, adminSignOut } from '../Functions/AdminAuth';

// Import property management functions
import {
    addProperty, updateProperty, deleteProperty,
    prepareArrayFields, stringToArray
} from '../Functions/AddProperties';

// Admin components
import PropertyForm from '../components/admin/PropertyForm';
import PropertyList from '../components/admin/PropertyList';
import Dashboard from '../components/admin/Dashboard';
import Settings from '../components/admin/Settings';
import BackToTop from "../components/BackToTop";
import EnquiriesManagement from '../components/admin/EnquiriesManagement';
import { FaMapMarkerAlt } from 'react-icons/fa';

const MainContent = styled.div`
  padding: 100px 30px 30px; // Increased padding from top and other sides
  transition: background-color 0.3s ease, color 0.3s ease;
  background-color: ${props => props.darkMode ? '#121212' : '#ffffff'};
  color: ${props => props.darkMode ? '#f5f5f5' : '#333333'};
  width: 100%;

  @media (max-width: 991px) {
    padding: 90px 20px 20px; // Increased padding for mobile as well
  }
`;

const ResponsiveContainer = styled.div`
  width: 100%;
  overflow-x: hidden;
  background-color: ${props => props.darkMode ? '#121212' : '#ffffff'};
  color: ${props => props.darkMode ? '#f5f5f5' : '#333333'};
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const PageContent = styled.div`
  display: flex;
  flex: 1;
  position: relative;
  flex-direction: column;
`;

// New tab components
const TabsContainer = styled.div`
  display: flex;
  background-color: ${props => props.darkMode ? '#1e1e1e' : '#f8f9fa'};
  border-bottom: 1px solid ${props => props.darkMode ? '#444' : '#dee2e6'};
  padding: 15px 20px; // Added vertical padding
  margin: -20px -20px 30px -20px; // Negative margins to stretch full width
  overflow-x: auto;
  position: sticky;
  top: 70px; // Stick below the header
  z-index: 10;
  box-shadow: 0 2px 10px rgba(0,0,0,${props => props.darkMode ? '0.3' : '0.05'});
  
  &::-webkit-scrollbar {
    height: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background-color: ${props => props.darkMode ? '#555' : '#ccc'};
    border-radius: 4px;
  }
  
  @media (max-width: 768px) {
    padding: 12px 15px;
  }
`;

const Tab = styled.button`
  padding: 15px 20px;
  border: none;
  background-color: transparent;
  color: ${props => props.active
        ? (props.darkMode ? '#3498db' : '#3498db')
        : (props.darkMode ? '#aaa' : '#666')};
  font-weight: ${props => props.active ? '600' : '400'};
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  min-width: max-content;
  
  &:hover {
    color: ${props => props.darkMode ? '#4aa3df' : '#2980b9'};
  }
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: ${props => props.active ? '80%' : '0'};
    height: 3px;
    background-color: ${props => props.darkMode ? '#3498db' : '#3498db'};
    transition: width 0.3s ease;
  }
  
  &:hover::after {
    width: ${props => props.active ? '80%' : '40%'};
  }
  
  display: flex;
  align-items: center;
  gap: 8px;
`;

const NotificationBadge = styled.span`
  background-color: #e74c3c;
  color: white;
  border-radius: 50%;
  width: 18px;
  height: 18px;
  font-size: 11px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
`;

const ContentContainer = styled(motion.div)`
  flex-grow: 1;
  padding-top: 15px;
`;

const AdminPage = () => {
    // Access dark mode state
    const { darkMode } = useDarkMode();
    const navigate = useNavigate();
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [authChecking, setAuthChecking] = useState(true);

    // Check authentication on component mount
    useEffect(() => {
        const unsubscribe = checkAuthState((user) => {
            setAuthChecking(false);
            if (user) {
                setIsAuthenticated(true);
            } else {
                // Redirect to login if not authenticated
                navigate('/admin-login');
            }
        });

        return () => unsubscribe();
    }, [navigate]);

    // Handle sign out
    const handleSignOut = async () => {
        const result = await adminSignOut({
            navigate,
            redirectTo: '/' // Redirect to homepage
        });
        // No need to manually navigate since the adminSignOut function now handles it
    };

    // State for active page (now it's active tab)
    const [activePage, setActivePage] = useState('dashboard');

    // State for properties
    const [properties, setProperties] = useState({});
    const [propertyIds, setPropertyIds] = useState([]);
    const [loading, setLoading] = useState(true);

    // Form state
    const [newProperty, setNewProperty] = useState({
        propertyTitle: '',
        propertyType: '',  // Add new property type field
        customPropertyType: '', // Add custom property type field
        location: '',
        description: '',
        startPrice: '',
        bhkCount: '',
        maxOccupancy: '',
        beds: '',
        baths: '',
        tag: '',
        mapLink: '',
        propertyHighlights: '',
        mainFeatures: '',
        amenities: '',
        coverImageUrl: '',
        additionalImagesUrl: '',
        brochureLink: '',
        mealsBrochure: '',
        houseRules: '',
        cancellationPolicy: '',
        paymentPolicy: ''
    });
    const [editingPropertyId, setEditingPropertyId] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    // State for array fields with better editing experience
    const [tempArrayFields, setTempArrayFields] = useState({
        propertyHighlights: [],
        mainFeatures: [],
        amenities: [],
        additionalImagesUrl: [],
        houseRules: [],
        cancellationPolicy: [],
        paymentPolicy: []
    });

    // State for tracking stats
    const [siteStats, setSiteStats] = useState({ totalVisits: 0, lastVisit: null });

    // State for booking enquiries
    const [bookingEnquiries, setBookingEnquiries] = useState([]);
    const [loadingEnquiries, setLoadingEnquiries] = useState(false);
    const [newEnquiriesCount, setNewEnquiriesCount] = useState(0);

    // Generate a unique ID for new properties
    const generatePropertyId = () => {
        return 'prop_' + Date.now() + '_' + Math.random().toString(36).substring(2, 9);
    };

    // Fetch properties from Firestore
    const fetchProperties = async () => {
        setLoading(true);
        try {
            const propertiesDocRef = doc(collection(db, 'properties'), 'allproperties');
            const docSnap = await getDoc(propertiesDocRef);

            if (docSnap.exists()) {
                const propertiesData = docSnap.data();
                setProperties(propertiesData);
                setPropertyIds(Object.keys(propertiesData));
            } else {
                // Create the document if it doesn't exist
                await setDoc(propertiesDocRef, {});
                setProperties({});
                setPropertyIds([]);
            }
        } catch (error) {
            console.error("Error fetching properties: ", error);
            toast.error("Failed to fetch properties: " + error.message);
        } finally {
            setLoading(false);
        }
    };

    // Fetch site statistics
    const fetchSiteStats = async () => {
        try {
            const stats = await getSiteStatistics();
            setSiteStats(stats);
        } catch (error) {
            console.error("Error fetching site statistics:", error);
            toast.error("Failed to fetch site statistics");
        }
    };

    // Fetch booking enquiries
    const fetchBookingEnquiries = async () => {
        try {
            setLoadingEnquiries(true);
            const enquiries = await getAllBookingEnquiries();
            setBookingEnquiries(enquiries);

            // Count new enquiries
            const newEnquiries = enquiries.filter(e => e.status === 'new').length;
            setNewEnquiriesCount(newEnquiries);
        } catch (error) {
            console.error("Error fetching booking enquiries:", error);
            toast.error("Failed to load booking enquiries");
        } finally {
            setLoadingEnquiries(false);
        }
    };

    useEffect(() => {
        fetchProperties();
        fetchSiteStats();
        fetchBookingEnquiries();
        // Add toast notification on initial load
        toast.info('Welcome to LSQ Stays Admin Panel');
    }, []);

    // Handle form input changes for new property
    const handleInputChange = (e) => {
        const { name, value } = e.target;

        // For number fields, ensure we have non-negative values
        if (name === 'startPrice' || name === 'bhkCount' || name === 'maxOccupancy' ||
            name === 'beds' || name === 'baths') {

            // Allow empty strings directly
            if (value === '') {
                setNewProperty({
                    ...newProperty,
                    [name]: value
                });
                return;
            }

            // For non-empty values, validate as a number
            const numValue = parseFloat(value);
            if (!isNaN(numValue) && numValue >= 0) {
                setNewProperty({
                    ...newProperty,
                    [name]: value // Keep original string to preserve decimal places
                });
            }
        } else {
            // For non-numeric fields, update normally
            setNewProperty({
                ...newProperty,
                [name]: value
            });
        }
    };

    // Handle form input changes for editing property
    const handleEditChange = (e) => {
        const { name, value } = e.target;
        const updatedProperty = { ...properties[editingPropertyId] };

        // For number fields, ensure we have non-negative values
        if (name === 'startPrice' || name === 'bhkCount' || name === 'maxOccupancy' ||
            name === 'beds' || name === 'baths') {

            // Allow empty strings directly
            if (value === '') {
                updatedProperty[name] = value;
            } else {
                // For non-empty values, validate as a number
                const numValue = parseFloat(value);
                if (!isNaN(numValue) && numValue >= 0) {
                    updatedProperty[name] = value; // Keep original string to preserve decimal places
                }
            }
        } else {
            // For non-numeric fields, update normally
            updatedProperty[name] = value;
        }

        // Update the properties state with the edited property
        setProperties({
            ...properties,
            [editingPropertyId]: updatedProperty
        });
    };

    // Handle array item changes (for better UX with array fields)
    const handleArrayItemChange = (fieldName, index, value) => {
        const updatedItems = [...tempArrayFields[fieldName]];
        updatedItems[index] = value;

        setTempArrayFields({
            ...tempArrayFields,
            [fieldName]: updatedItems
        });
    };

    // Add a new item to an array field
    const handleAddArrayItem = (fieldName, value = '') => {
        console.log("Adding item to", fieldName, ":", value);

        // If a value is provided, use it, otherwise add an empty string
        const newValue = value.trim() !== '' ? value : '';
        const updatedItems = [...(tempArrayFields[fieldName] || []), newValue];

        console.log("Updated array:", updatedItems);

        setTempArrayFields({
            ...tempArrayFields,
            [fieldName]: updatedItems
        });
    };

    // Remove an item from an array field
    const handleRemoveArrayItem = (fieldName, index) => {
        const updatedItems = [...tempArrayFields[fieldName]];
        updatedItems.splice(index, 1);

        setTempArrayFields({
            ...tempArrayFields,
            [fieldName]: updatedItems
        });
    };

    // Handle drag and drop reordering
    const handleDragEnd = (result, fieldName) => {
        if (!result.destination) return;

        const items = Array.from(tempArrayFields[fieldName]);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);

        setTempArrayFields({
            ...tempArrayFields,
            [fieldName]: items
        });
    };

    // Add new property to Firestore
    const handleAddProperty = async (e) => {
        e.preventDefault();

        const result = await addProperty(newProperty, tempArrayFields, fetchProperties);

        if (result.success) {
            // Reset form
            setNewProperty({
                propertyTitle: '',
                propertyType: '',  // Reset property type
                customPropertyType: '', // Reset custom property type
                location: '',
                description: '',
                startPrice: '',
                bhkCount: '',
                maxOccupancy: '',
                beds: '',
                baths: '',
                tag: '',
                mapLink: '',
                propertyHighlights: '',
                mainFeatures: '',
                amenities: '',
                coverImageUrl: '',
                additionalImagesUrl: '',
                brochureLink: '',
                mealsBrochure: '',
                houseRules: '',
                cancellationPolicy: '',
                paymentPolicy: ''
            });

            // Reset array fields
            setTempArrayFields({
                propertyHighlights: [],
                mainFeatures: [],
                amenities: [],
                additionalImagesUrl: [],
                houseRules: [],
                cancellationPolicy: [],
                paymentPolicy: []
            });
        }
    };

    // Set up property for editing
    const handleEditPropertyStart = (propertyId) => {
        setEditingPropertyId(propertyId);
        setIsEditing(true);

        // Prepare array fields for editing
        setTempArrayFields(prepareArrayFields(properties[propertyId]));

        // Add scroll to top behavior for better user experience
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Save edited property to Firestore
    const handleSaveProperty = async (e) => {
        e.preventDefault();

        const propertyData = properties[editingPropertyId];
        const result = await updateProperty(editingPropertyId, propertyData, tempArrayFields, fetchProperties);

        if (result.success) {
            setIsEditing(false);
            setEditingPropertyId(null);

            // Reset array fields
            setTempArrayFields({
                propertyHighlights: [],
                mainFeatures: [],
                amenities: [],
                additionalImagesUrl: [],
                houseRules: [],
                cancellationPolicy: [],
                paymentPolicy: []
            });
        }
    };

    // Delete property from Firestore
    const handleDeleteProperty = async (propertyId) => {
        if (window.confirm("Are you sure you want to delete this property?")) {
            await deleteProperty(propertyId, fetchProperties);
        }
    };

    // Cancel editing
    const handleCancelEdit = () => {
        setIsEditing(false);
        setEditingPropertyId(null);

        // Reset array fields
        setTempArrayFields({
            propertyHighlights: [],
            mainFeatures: [],
            amenities: [],
            additionalImagesUrl: [],
            houseRules: [],
            cancellationPolicy: [],
            paymentPolicy: []
        });

        fetchProperties(); // Refresh data to discard any unsaved changes
    };

    // Render the correct page based on activePage state
    const renderActivePage = () => {
        // Add page transitions
        const pageTransitionProps = {
            initial: { opacity: 0, y: 20 },
            animate: { opacity: 1, y: 0 },
            exit: { opacity: 0, y: -20 },
            transition: { duration: 0.3 }
        };

        switch (activePage) {
            case 'dashboard':
                // Log the properties data to verify it contains viewCount
                const propertiesArray = Object.entries(properties).map(([id, prop]) => ({
                    id,
                    ...prop
                }));

                return (
                    <motion.div {...pageTransitionProps} key="dashboard">
                        <Dashboard
                            properties={propertiesArray}
                            darkMode={darkMode}
                            siteStats={siteStats}
                            bookingEnquiries={bookingEnquiries} // Make sure this is passed correctly
                            loadingEnquiries={loadingEnquiries}
                            refreshEnquiries={fetchBookingEnquiries}
                            setActivePage={setActivePage} // This should be properly referenced
                        />
                    </motion.div>
                );
            case 'enquiries':
                // Replace placeholder with the EnquiriesManagement component
                return (
                    <motion.div {...pageTransitionProps} key="enquiries">
                        <EnquiriesManagement
                            bookingEnquiries={bookingEnquiries}
                            loadingEnquiries={loadingEnquiries}
                            refreshEnquiries={fetchBookingEnquiries}
                            darkMode={darkMode}
                        />
                    </motion.div>
                );
            case 'properties':
                return (
                    <motion.div {...pageTransitionProps} key="properties">
                        <AnimatePresence mode="wait">
                            {!isEditing ? (
                                <PropertyForm
                                    isEditing={false}
                                    formData={newProperty}
                                    handleSubmit={handleAddProperty}
                                    handleChange={handleInputChange}
                                    tempArrayFields={tempArrayFields}
                                    setTempArrayFields={setTempArrayFields}
                                    handleArrayItemChange={handleArrayItemChange}
                                    handleAddArrayItem={handleAddArrayItem}
                                    handleRemoveArrayItem={handleRemoveArrayItem}
                                    handleDragEnd={handleDragEnd}
                                    darkMode={darkMode}
                                />
                            ) : (
                                <PropertyForm
                                    isEditing={true}
                                    formData={properties[editingPropertyId] || {}}
                                    handleSubmit={handleSaveProperty}
                                    handleChange={handleEditChange}
                                    handleCancelEdit={handleCancelEdit}
                                    tempArrayFields={tempArrayFields}
                                    setTempArrayFields={setTempArrayFields}
                                    handleArrayItemChange={handleArrayItemChange}
                                    handleAddArrayItem={handleAddArrayItem}
                                    handleRemoveArrayItem={handleRemoveArrayItem}
                                    handleDragEnd={handleDragEnd}
                                    darkMode={darkMode}
                                />
                            )}
                        </AnimatePresence>

                        <PropertyList
                            loading={loading}
                            properties={properties}
                            propertyIds={propertyIds}
                            searchTerm={searchTerm}
                            setSearchTerm={setSearchTerm}
                            handleEditPropertyStart={handleEditPropertyStart}
                            handleDeleteProperty={handleDeleteProperty}
                            darkMode={darkMode}
                        />
                    </motion.div>
                );
            case 'settings':
                return (
                    <motion.div {...pageTransitionProps} key="settings">
                        <Settings darkMode={darkMode} />
                    </motion.div>
                );
            default:
                return null;
        }
    };

    // If still checking auth or not authenticated, show loading
    if (authChecking) {
        return (
            <div className={`admin-page ${darkMode ? 'dark-mode' : ''}`} style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                backgroundColor: darkMode ? '#121212' : '#f8f9fa'
            }}>
                <div className="text-center">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-3">Verifying authentication...</p>
                </div>
            </div>
        );
    }

    if (!isAuthenticated) {
        return null; // Will redirect to login
    }

    return (
        <ResponsiveContainer className={`admin-page ${darkMode ? 'dark-mode' : ''}`} darkMode={darkMode}>
            <Header currentPage="admin" />
            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop={true}
                theme={darkMode ? 'dark' : 'light'}
            />

            <PageContent>
                <MainContent darkMode={darkMode}>
                    <TabsContainer darkMode={darkMode}>
                        <Tab
                            active={activePage === 'dashboard'}
                            darkMode={darkMode}
                            onClick={() => setActivePage('dashboard')}
                        >
                            <FaTachometerAlt size={16} /> Dashboard
                        </Tab>
                        <Tab
                            active={activePage === 'enquiries'}
                            darkMode={darkMode}
                            onClick={() => setActivePage('enquiries')}
                        >
                            <FaInbox size={16} />
                            Enquiries
                            {newEnquiriesCount > 0 && (
                                <NotificationBadge>{newEnquiriesCount}</NotificationBadge>
                            )}
                        </Tab>
                        <Tab
                            active={activePage === 'properties'}
                            darkMode={darkMode}
                            onClick={() => setActivePage('properties')}
                        >
                            <FaBuilding size={16} /> Properties
                        </Tab>
                        <Tab
                            active={activePage === 'settings'}
                            darkMode={darkMode}
                            onClick={() => setActivePage('settings')}
                        >
                            <FaCog size={16} /> Settings
                        </Tab>
                        <Tab
                            darkMode={darkMode}
                            onClick={handleSignOut}
                            style={{ marginLeft: 'auto' }}
                        >
                            <FaSignOutAlt size={16} /> Logout
                        </Tab>
                    </TabsContainer>

                    <AnimatePresence mode="wait">
                        <ContentContainer
                            key={activePage}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.2 }}
                        >
                            {renderActivePage()}
                        </ContentContainer>
                    </AnimatePresence>
                </MainContent>
            </PageContent>

            <Footer />
            <BackToTop />
        </ResponsiveContainer>
    );
};

export default AdminPage;
